import styled from '@emotion/styled';
import { CommentIndexWrapper } from '@seedlang/hoc';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import CommentCreate from 'components/comment/comment_create';
import CommentShow from 'components/comment/comment_show';
import InPlaceSelect from 'components/form/in_place_select';
import ForumThreadPost from 'components/forum_thread/forum_thread_post';
import Spinner from 'components/spinner';
import Text from 'components/text';
import NoAccessButton from 'components/worksheet/no_access_button';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import SoftButton from 'components/button/soft_button';
import { Theme } from '@seedlang/constants';

const Wrapper = styled.div`
  margin: ${props => props.margin};
  font-size: 12px;
  width: 100%;
  background: white;
  border-radius: ${props => props.borderRadius};
  padding: ${props => props.commentLayout === 'forum' ? props.forumPadding || '30px 35px 30px 30px' : props.padding};
  box-sizing: ${props => props.forumPadding ? 'border-box' : null};
  select {
    height: 30px;
  }
`;

const CommentsRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
  border-top: ${props => props.borderTop};
`;

const SelectWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  align-items: center;
`;

const CommentCreateWrapper = styled.div`
  position: relative;
  pointer-events: ${props => props.hasNoAccess ? 'none' : 'auto'};
`;

const CommentCreateOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.8));
  pointer-events: none;
  z-index: 1;
`;

const NoAccessButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: auto;
  background: white;
`;

const ScrollButton = styled(SoftButton)`
  height: 35px;
`;

const SortSelect = styled(InPlaceSelect)`
  select {
    background: ${Theme.softButtonBackground};
    border: 0;
    border-radius: 4px;
    color: #333;
    font-family: inherit;
    font-size: 11px;
    font-weight: 400;
    letter-spacing: 0.04em;
    margin: 0 5px 0 0;
    padding: 0 12px;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.65);
    text-transform: uppercase;
  }
`;

@observer
class CommentIndex extends React.Component {
  @observable selectedCommentId = null;

  constructor(props) {
    super(props);
    this.selectedCommentId = isPresent(window.location.hash) ? window.location.hash.replace('#comment-', '') : null;
  }

  static defaultProps = {
    margin: '0 0 20px 0',
    padding: '20px',
    borderRadius: '5px',
  };
  get commentLayout() {
    if (this.props.commentLayout) {
      return this.props.commentLayout;
    } else if (this.props.showForumThreadPost) {
      return 'forum';
    } else if (this.props.compactCommentLayout) {
      return 'compact';
    }
    return 'embedded';
  }

  scrollToLatestComment = () => {
    if (isPresent(this.props.latestCommentId)) {
      this.selectedCommentId = this.props.latestCommentId;
      const element = document.getElementById(`comment-${this.props.latestCommentId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
  handleHash = () => {
    const hash = window.location.hash;
    if (hash) {
      const checkForElement = () => {
        const element = document.querySelector(hash);
        if (element) {
          setTimeout(() => {
            const rect = element.getBoundingClientRect();
            const isVisible = (
              rect.top >= 0 &&
              rect.left >= 0 &&
              rect.bottom <= window.innerHeight &&
              rect.right <= window.innerWidth
            );
            if (!isVisible) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 300);
        } else {
          setTimeout(checkForElement, 100);
        }
      };
      checkForElement();
    }
  }

  componentDidMount() {
    if (isPresent(this.props.comments) && this.props.comments.length > 0) {
      this.handleHash();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.comments.length === 0 && this.props.comments.length > 0) {
      this.handleHash();
    }
  }

  render() {
    return (
      <Wrapper
        margin={this.props.margin}
        padding={this.props.padding}
        borderRadius={this.props.borderRadius}
        commentLayout={this.commentLayout}
        forumPadding={this.props.forumPadding}
      >
        {
          this.props.showForumThreadPost &&
          <ForumThreadPost
            hideUserProfile={this.props.hideUserProfile}
            groupId={this.props.groupId}
            inModal={this.props.inModal}
            post={this.props.post}
            user={AppUI.user}
            onChange={this.props.onChange}
            onUpdateForumThread={this.props.onUpdateForumThread}
            hideForumThreadDetails={this.props.hideForumThreadDetails}
            hideSpecialCharacters={this.props.hideSpecialCharacters || this.props.post?.hideSpecialCharacters}
            languageId={this.props.languageId || AppUI.targetLanguageId}
            compactCommentLayout={this.props.compactCommentLayout}
          />
        }
        {
          (this.props.showCommentCreate || this.props.showNoAccessButton) &&
          <CommentCreateWrapper hasNoAccess={this.props.showNoAccessButton}>
            <CommentCreate
              debugInfo={this.props.debugInfo}
              showXp={this.props.showXp}
              blockTextExpansion={this.props.blockTextExpansion}
              wordId={this.props.wordId}
              sentenceId={this.props.sentenceId}
              conceptId={this.props.conceptId}
              namespace={this.props.namespace || this.props.translationId || this.props.conceptId || this.props.forumThreadId}
              commentable={this.props.commentable}
              commentableType={this.props.commentableType}
              commentableId={this.props.commentableId}
              afterCreateComment={this.props.onChange}
              languageId={this.props.languageId || AppUI.targetLanguageId}
              placeholder={this.props.placeholder}
              hideSpecialCharacters={this.props.hideSpecialCharacters || this.props.post?.hideSpecialCharacters}
              margin='0 0 10px 0'
            />
            {
              this.props.showNoAccessButton &&
              <>
                <CommentCreateOverlay />
                <NoAccessButtonWrapper>
                  <NoAccessButton margin='0' reason={this.props.noAccessReason} />
                </NoAccessButtonWrapper>
              </>
            }
          </CommentCreateWrapper>
        }
        <span id='page-top' />
        {
          this.props.showSpinner &&
          <Spinner />
        }
        {
          this.props.comments.length > 1 && AppUI.userIsLoaded &&
          <CommentsRow
            borderTop={this.props.showCommentCreate ? '1px solid #dedede' : ''}
          >
            {
              this.props.showCommentCount &&
              <Text
                bold
              >
                {this.props.compactCommentLayout ? 'Comments' : `${AppUI.user.commentOrdering === 'created_at' ? 'Oldest' : 'Newest'} Comments`}
              </Text>
            }
            {
              !this.props.hideCommentControls &&
                <SelectWrapper>
                  {
                    this.props.comments.length > 5 &&
                    <ScrollButton onClick={this.scrollToLatestComment}>
                      Go to Newest
                    </ScrollButton>
                  }
                  <SortSelect
                    allowUpdate
                    value={AppUI.user.commentOrdering}
                    onChange={this.props.setCommentOrdering}
                    options={[
                      ['created_at', 'Sort by Old'],
                      ['-created_at', 'Sort by New'],
                    ]}
                  />
                </SelectWrapper>
            }
          </CommentsRow>
        }
        {
          this.props.comments.map(item => {
            if (!item.hidden ||
              item.user.id === AppUI.user.id ||
              (item.respondToId === AppUI.user.id && item.user.anyAdminOrModerator) ||
              AppUI.user.admin ||
              AppUI.user.moderator) {
              return (
                <CommentShow
                  key={item.id}
                  hideUserProfile={this.props.hideUserProfile}
                  indentation={this.props.indentation}
                  onChange={this.props.onChange}
                  commentable={this.props.commentable}
                  commentableType={this.props.commentableType}
                  commentableId={this.props.commentableId}
                  comment={item}
                  user={this.props.user}
                  fontSize={this.props.fontSize}
                  userProfileImageSize={this.props.userProfileImageSize}
                  margin={this.props.commentMargin}
                  padding={this.props.commentPadding || this.props.padding}
                  languageId={this.props.languageId || AppUI.targetLanguageId}
                  selected={this.selectedCommentId === item.id}
                  commentLayout={this.commentLayout}
                  compactCommentLayout={this.props.compactCommentLayout}
                  hideSpecialCharacters={this.props.hideSpecialCharacters || this.props.post?.hideSpecialCharacters}
                />
              );
            } else {
              return null;
            }
          })
        }
      </Wrapper>
    );
  }
}

export default CommentIndexWrapper(CommentIndex);
